import { z } from "zod";
import type { NoticeKind } from "./notices.js";
import { Product } from "./product.js";

/**
 * 問い合わせとお知らせ（#124 の 3a、docs/50）。製品の画面・製品の API・管理アプリ（受け口）が同じ形を見る。
 *   - 画面 → 製品の API：SupportTicketRequest（ログイン後は会社・人・メールを API が足す）・SupportReplyRequest（メールのリンクから）
 *   - 製品の API → 管理サーバーの受け口（ローカル NW・署名つき）：IntakeTicket・IntakeReply・IntakeThread
 *   - 製品のホームの帯：Announcement
 */

/**
 * 問い合わせの種類（画面の選択肢と管理アプリの表示。順番もこのまま）。#148（docs/58 §10.2）で「要望」を足して7つ。
 * 入るもの・入らないもの（AI に渡す定義）は supportTriage.ts の SUPPORT_CATEGORY_DEFS
 */
export const SUPPORT_CATEGORIES = {
  usage: "使い方",
  bug: "不具合・エラー",
  request: "要望",
  billing: "料金・契約",
  account: "アカウント・ログイン",
  data: "データ・退会",
  other: "その他",
} as const;
export const SupportCategory = z.enum(["usage", "bug", "request", "billing", "account", "data", "other"]);
export type SupportCategory = z.infer<typeof SupportCategory>;

/** 添付の画像（画面で base64 にして送る）。種類は API が中身で確かめ直す */
export const SUPPORT_LIMITS = {
  /** 本文 */
  bodyMax: 4000,
  /** 画像の枚数・1枚・合わせて */
  files: 3,
  fileBytes: 5 * 1024 * 1024,
  totalBytes: 10 * 1024 * 1024,
  /** 画面の写し（「？」を押したときの画面。画像の3枚・10MB とは別に1枚） */
  captureBytes: 3 * 1024 * 1024,
  /** 返事のリンクの日数（受け口が作る署名つきのリンク） */
  linkDays: 7,
} as const;
export const SUPPORT_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;
export const SupportAttachment = z.object({
  name: z.string().trim().min(1).max(120),
  type: z.enum(SUPPORT_IMAGE_TYPES),
  /** base64（data: の頭は付けない）。5MB の画像で約 6.7MB */
  data: z.string().min(4).max(Math.ceil((SUPPORT_LIMITS.fileBytes * 4) / 3) + 8).regex(/^[A-Za-z0-9+/]+={0,2}$/, "画像の形が読めません"),
});
export type SupportAttachment = z.infer<typeof SupportAttachment>;

/**
 * 画面の写し（ログイン後の「？」の窓だけ。docs/50 §7）。窓の後ろの、見えている範囲を画面が PNG（大きければ JPEG）にして送る。
 * パスワード・確認コード・カード番号の欄は画面が塗りつぶしてから送る。種類は API が中身で確かめ直す
 */
export const SupportCapture = z.object({
  type: z.enum(["image/png", "image/jpeg"]),
  data: z.string().min(4).max(Math.ceil((SUPPORT_LIMITS.captureBytes * 4) / 3) + 8).regex(/^[A-Za-z0-9+/]+={0,2}$/, "画像の形が読めません"),
});
export type SupportCapture = z.infer<typeof SupportCapture>;

const Body = z.string().trim().min(1, "内容を書いてください").max(SUPPORT_LIMITS.bodyMax, `内容は ${SUPPORT_LIMITS.bodyMax}字までです`);

/**
 * 「？」を押す前の足あと（#144、docs/53 §8.4）。画面の版（build に埋めた git のコミット）と直前の操作 20件：
 * 開いた画面（パス、id は伏せる）・押したボタン（ラベル。入力した内容は入れない）・GET 以外か 2xx でない API・画面のエラー・
 * 「？」の窓で読んだヘルプの記事（#150。題と記事の ID・解決したか）。ログイン後の「？」だけ
 */
export const SUPPORT_TRAIL_MAX = 20;
export const SupportTrailItem = z.object({
  /** 時刻（ISO） */
  t: z.iso.datetime(),
  /** help＝「？」の窓のヘルプの記事（開いた・解決した・しなかった。#150。記事の ID を括弧に） */
  kind: z.enum(["nav", "click", "api", "error", "help"]),
  text: z.string().max(200),
});
export type SupportTrailItem = z.infer<typeof SupportTrailItem>;
export const SupportTrail = z.object({ build: z.string().trim().max(40), items: z.array(SupportTrailItem).max(SUPPORT_TRAIL_MAX) });
export type SupportTrail = z.infer<typeof SupportTrail>;

/** 画面 → 製品の API。ログイン前（/contact）はメールが要る。website は見えない欄（入っていたら受けたふりをして捨てる） */
export const SupportTicketRequest = z.object({
  category: SupportCategory,
  body: Body,
  email: z.email("メールアドレスの形を確かめてください").max(254).optional(),
  name: z.string().trim().max(60).optional(),
  /** 開いていた画面（パス。id は画面が :id に伏せてから送る）・題 */
  screen: z.string().trim().max(200).optional(),
  title: z.string().trim().max(120).optional(),
  /** 画面の幅（px） */
  width: z.number().int().min(0).max(10000).optional(),
  attachments: z.array(SupportAttachment).max(SUPPORT_LIMITS.files).optional(),
  /** 画面の写し（ログイン後だけ。ログイン前に付いていても API は使わない） */
  capture: SupportCapture.optional(),
  /** 画面の版と直前の操作（ログイン後だけ。ログイン前に付いていても API は使わない） */
  trail: SupportTrail.optional(),
  website: z.string().max(200).optional(),
});
export type SupportTicketRequest = z.infer<typeof SupportTicketRequest>;

export const SupportReplyRequest = z.object({
  body: Body,
  attachments: z.array(SupportAttachment).max(SUPPORT_LIMITS.files).optional(),
  website: z.string().max(200).optional(),
});
export type SupportReplyRequest = z.infer<typeof SupportReplyRequest>;

/** 受け付けた（番号は受付のメールで知らせる。管理サーバーへは送り箱から送る） */
export type SupportAccepted = { ok: true };

/** 返事のリンクのページ（やりとりの一覧）。運営の社内メモは入らない */
/** 画像：id があれば返事のリンクのページで見せる（/support/t/:token/files/:id。ログインしてから送った問い合わせだけ） */
export type SupportThreadFile = { name: string; id?: string };
export type SupportThreadMessage = { from: "customer" | "operator"; name: string; body: string; at: string; files: SupportThreadFile[] };
export type SupportThread = {
  no: string;
  product: Product;
  category: SupportCategory;
  status: "open" | "closed";
  messages: SupportThreadMessage[];
  /** 期限の切れたリンク（中身は見せない） */
  expired?: false;
};

/** 返事のリンクの形（受け口が作る。id.期限.署名） */
export const SUPPORT_TOKEN_RE = /^[0-9]{1,12}\.[0-9]{8,12}\.[A-Za-z0-9_-]{22,64}$/;

// ---------------------------------------------------------------------------
// 製品の API → 管理サーバーの受け口（ローカル NW 192.168.10.21:3101、HMAC-SHA256 の署名つき）
// ---------------------------------------------------------------------------

/** 署名のヘッダー：時刻（秒）と、`${時刻}.${method} ${path}.${本文}` の HMAC-SHA256（16進） */
export const INTAKE_HEADERS = { ts: "x-gb-intake-ts", sig: "x-gb-intake-sig" } as const;
/** 時刻のずれの許し（秒） */
export const INTAKE_SKEW_SEC = 300;

const Device = z.enum(["pc", "phone", "other"]);
export const IntakeTicket = z.object({
  /** 送り箱の id（同じものを2回送っても1件） */
  ref: z.uuid(),
  product: Product,
  category: SupportCategory,
  body: Body,
  email: z.email().max(254),
  name: z.string().trim().max(60).default(""),
  /** ログイン後だけ */
  tenantId: z.uuid().nullable().default(null),
  userId: z.uuid().nullable().default(null),
  companyName: z.string().trim().max(200).default(""),
  screen: z.string().trim().max(200).default(""),
  title: z.string().trim().max(120).default(""),
  device: Device.default("other"),
  browser: z.string().max(300).default(""),
  width: z.number().int().min(0).max(10000).nullable().default(null),
  attachments: z.array(SupportAttachment).max(SUPPORT_LIMITS.files).default([]),
  capture: SupportCapture.nullable().default(null),
  trail: SupportTrail.nullable().default(null),
  at: z.iso.datetime(),
});
export type IntakeTicket = z.input<typeof IntakeTicket>;

export const IntakeReply = z.object({
  ref: z.uuid(),
  token: z.string().regex(SUPPORT_TOKEN_RE),
  body: Body,
  attachments: z.array(SupportAttachment).max(SUPPORT_LIMITS.files).default([]),
  device: Device.default("other"),
  browser: z.string().max(300).default(""),
  at: z.iso.datetime(),
});
export type IntakeReply = z.input<typeof IntakeReply>;

/** 受け口の答え（チケット・返事）。410 TOKEN_EXPIRED／404 TOKEN_BAD はリンクのページの言葉にする */
export type IntakeResult = { ok: true; no: string; duplicate?: boolean };

// ---------------------------------------------------------------------------
// お知らせの帯（製品のホームの上。閉じた人には出さない）。区分 kind は docs/95（notices.ts の NoticeKind）
// ---------------------------------------------------------------------------
export type Announcement = { id: string; kind: NoticeKind; title: string; body: string; endsAt: string };
export type AnnouncementsResponse = { items: Announcement[] };

export const SUPPORT_MSG = {
  title: "お問い合わせ",
  /** 左の列・☰ の「ヘルプ」の読み上げ名（#187：前は上の帯の「？」で「お問い合わせ」） */
  open: "ヘルプ・お問い合わせ",
  /** 左の列・☰ の項目の名前（#187） */
  navHelp: "ヘルプ",
  category: "種類",
  body: "内容",
  files: "画像（3枚まで）",
  email: "メールアドレス",
  name: "お名前（任意）",
  send: "送信",
  sent: "受け付けました。受付のメールをお送りします。",
  hours: "返信は平日 10:00〜17:00、1営業日以内にメールでお送りします。",
  auto: "開いている画面の名前と、端末の種類も一緒に送ります。",
  capture: "この画面の写しを添える",
  capturing: "画面の写しを作成しています…",
  captureTooBig: "画面の写しは3MBまでです",
  captureAlt: "添える画面の写し",
  autoShot: "画面の写し（パスワードなどの欄は塗りつぶします）・開いている画面の名前・端末の種類も一緒に送ります。",
  fileTooBig: `画像は1枚 5MB・合わせて 10MB までです`,
  fileType: "画像は PNG・JPEG・WebP・GIF だけ送れます",
  replyTitle: "お問い合わせの続き",
  reply: "返信",
  replied: "送信しました。担当者からの返信をお待ちください。",
  expired: "このリンクの期限が切れました。お問い合わせから新しく送ってください。",
  bad: "このリンクは使えません。お問い合わせから新しく送ってください。",
  unavailable: "いまは表示できません。少し時間をおいて開き直してください。",
  closed: "このお問い合わせは完了しています。返信すると、もう一度担当者に届きます。",
  loginTrouble: "ログインできないときは",
  close: "閉じる",
} as const;
